
// services/ReportTypeService.js - FIXED imports
import { BaseService } from './BaseService';
import { ReportType, ReportTypeNames, ReportTypeFavorites } from '../models';
import { FavoritesRequestDTO, FavoritesResponseDTO } from '../models/FavoritesDTO';

export class ReportTypeService extends BaseService {
  constructor() {
    super();
  }

  async getNames() {
    // New unified endpoint that returns all report types including favorites and specialty info
    const response = await this.get('/report-types/available');

    // Handle new response format: { success: true, data: [...] }
    const payload = response?.data ?? response;
    const list = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
        ? payload
        : [];

    // Transform snake_case fields from API to camelCase for the model
    const transformedList = list.map(item => ({
      id: item.id,
      name: item.name,
      description: item.description,
      specialty: item.specialty,
      isFavorite: item.is_favorite,
      isEnterprise: item.is_enterprise,
      isEnterpriseEnabled: item.is_enterprise_enabled,
      isPersonal: item.is_personal,
      // Keep backward compatibility - mark non-personal, non-enterprise as default
      isDefault: !item.is_personal && !item.is_enterprise,
      customInstruction: item.custom_instruction || null,
      displayOrder: item.display_order
    }));

    return new ReportTypeNames(transformedList);
  }


  async determineReportType(transcription, candidates = undefined) {
    const payload = { transcription };
    if (Array.isArray(candidates) && candidates.length > 0) {
      payload.candidates = candidates;
    }
    const response = await this.post('/reports/determine-type', payload);
    const data = response.data || response;
    return data.reportType;
  }

  async createReportType(data) {
    // data may include:
    // - name: string (required)
    // - customInstruction: string (optional) — user-level instruction saved with the type
    const response = await this.post('/report-types', data);
    const responseData = response.data || response;
    return new ReportType(responseData.reportType);
  }


  async createReportTypeWithExamples(data) {
    // data may include:
    // - name: string (required)
    // - examples: string[] (optional)
    // - customInstruction: string (optional) — user-level instruction saved with the type
    const response = await this.post('/report-types/with-examples', data);
    const responseData = response.data || response;
    return new ReportType(responseData.reportType);
  }

  async deleteReportType(name) {
    await this.delete(`/report-types/${name}`);
  }

  async getFavorites() {
    const response = await this.get('/favorites');
    const favorites = response?.data?.data?.favorites
      || response?.data?.favorites
      || response?.favorites
      || response;
    return new ReportTypeFavorites(favorites);
  }


  async replaceFavorites(favorites) {
    // Create the request DTO
    const requestDTO = new FavoritesRequestDTO(favorites);
    
    const response = await this.put('/favorites', requestDTO);
    
    // Parse response using DTO
    const responseDTO = new FavoritesResponseDTO(response.data || response);
    
    // Return the favorites from the response
    return responseDTO.getFavorites();
  }

  async getBySpecialty() {
    // Now we extract specialty grouping from the unified /available endpoint
    // This avoids a separate API call
    const reportTypeNames = await this.getNames();
    const allTypes = reportTypeNames.getAll();

    // Group by specialty
    const bySpecialty = {};
    allTypes.forEach(type => {
      const specialty = type.specialty || 'general';
      if (!bySpecialty[specialty]) {
        bySpecialty[specialty] = [];
      }
      bySpecialty[specialty].push({
        name: type.name,
        description: type.description
      });
    });

    return bySpecialty;
  }

  async resetFavorites(specialty) {
    const response = await this.post('/report-types/reset-favorites', {specialty: specialty || 'general'});
    return response.data;
  }
}

export default ReportTypeService;
